import React, { useEffect, useState } from 'react';
import { PiHouseLight } from 'react-icons/pi';
import type { PropertyCardImageProps } from 'src/modules/shared/types/catalog/property-card-image.type';
import { Text } from 'src/modules/shared/components/atoms/text';

/** Shows the property photo, loading state, or the shared house fallback. */
export const PropertyCardImage: React.FC<PropertyCardImageProps> = ({
  imageUrl,
  alt,
  loading,
  emptyLabel,
  compact = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [imageUrl]);

  const showImage = Boolean(imageUrl) && !hasError;
  const showLoadingState = loading || (showImage && !isLoaded);
  const showEmptyState = !showLoadingState && !showImage;

  return (
    <>
      {showImage ? (
        <img
          src={imageUrl ?? undefined}
          alt={alt}
          className={`h-full w-full object-cover transition-opacity duration-200 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      ) : null}

      {showLoadingState ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.14),_transparent_55%),linear-gradient(160deg,_#f8fafc,_#e2e8f0)]"
        />
      ) : null}

      {showEmptyState ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.14),_transparent_55%),linear-gradient(160deg,_#f8fafc,_#e2e8f0)] text-center">
          <PiHouseLight
            className={
              compact ? 'h-5 w-5 text-slate-400' : 'h-10 w-10 text-slate-400'
            }
            aria-hidden="true"
          />
          {!compact && (
            <Text size="small" weight="medium" className="px-6 text-slate-700">
              {emptyLabel}
            </Text>
          )}
        </div>
      ) : null}
    </>
  );
};
